import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, TrendingUp, TrendingDown, DollarSign, Users, Copy, ExternalLink, Search, Filter } from 'lucide-react';

const API = 'https://api.naijapredict.net';
async function apiFetch(path) {
  const token = localStorage.getItem('creator_token');
  const res = await fetch(API + path, { headers: { Authorization: 'Bearer ' + token } });
  return res.json();
}

function MarketCard({ market }) {
  const stateColors = { 0:'#00c566', 1:'#f5c842', 2:'#ff4444' };
  const stateLabels = { 0:'Active', 1:'Resolved', 2:'Cancelled' };
  const StateIcon   = { 0:Clock, 1:CheckCircle, 2:XCircle }[market.state];
  const volume = parseFloat(market.totalCollateral||0);
  const earnings = volume * 0.005;

  function copyLink() {
    navigator.clipboard?.writeText(`https://app.naijapredict.net/dollar/market/${market.id}`);
    alert('Market link copied!');
  }

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
        <div style={{ flex:1, marginRight:16 }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#e8eaeb', marginBottom:6, lineHeight:1.4 }}>{market.question}</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <span style={{ background:'rgba(0,197,102,.08)', border:'1px solid rgba(0,197,102,.15)', borderRadius:6, padding:'2px 8px', fontSize:11, color:'#00c566', fontWeight:600 }}>{market.category}</span>
            <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:stateColors[market.state], fontWeight:600 }}>
              <StateIcon size={11} strokeWidth={2}/>{stateLabels[market.state]}
            </span>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={copyLink} style={{ width:36, height:36, borderRadius:8, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#4a5a63' }}>
            <Copy size={14}/>
          </button>
          <a href={`https://sepolia.etherscan.io/address/${market.contract||'0xDF4aA10eD23D6F22325284c48b7cDf36a3daE577'}`} target="_blank" rel="noreferrer"
            style={{ width:36, height:36, borderRadius:8, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'center', color:'#4a5a63' }}>
            <ExternalLink size={14}/>
          </a>
        </div>
      </div>

      {/* Odds Bar */}
      <div style={{ marginBottom:12 }}>
        <div style={{ display:'flex', borderRadius:6, overflow:'hidden', height:8, marginBottom:6, gap:2 }}>
          <div style={{ flex:market.yesPct||50, background:'#00c566', borderRadius:'4px 0 0 4px' }}/>
          <div style={{ flex:market.noPct||50, background:'#ff4444', borderRadius:'0 4px 4px 0' }}/>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
          <span style={{ color:'#00c566', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}><TrendingUp size={11}/> YES {market.yesPct||50}%</span>
          <span style={{ color:'#ff4444', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}><TrendingDown size={11}/> NO {market.noPct||50}%</span>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
        {[
          [DollarSign, 'Pool Size',   `$${volume.toFixed(2)}`],
          [Users,      'Min Volume',  `$${market.minVolume||50}`],
          [DollarSign, 'My Earnings', `$${earnings.toFixed(4)}`],
        ].map(([Icon,label,val])=>(
          <div key={label} style={{ background:'rgba(255,255,255,.03)', borderRadius:8, padding:'10px 12px' }}>
            <div style={{ fontSize:10, color:'#4a5a63', marginBottom:4, fontWeight:600 }}>{label}</div>
            <div style={{ fontSize:14, fontWeight:700, color:'#e8eaeb' }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop:10, fontSize:11, color:'#4a5a63', display:'flex', alignItems:'center', gap:4 }}>
        <Clock size={10}/> Expires: {new Date((market.expiresAt||Date.now())).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'})}
      </div>
    </div>
  );
}

export default function Markets() {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('all');

  useEffect(() => {
    apiFetch('/api/creator/markets').then(setMarkets).finally(()=>setLoading(false));
  }, []);

  const filtered = markets.filter(m => {
    const matchSearch = m.question.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter==='all' || String(m.state)===({'active':'0','resolved':'1','cancelled':'2'}[filter]||filter);
    return matchSearch && matchFilter;
  });

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:28, fontWeight:800, fontFamily:"'Syne',sans-serif", marginBottom:4 }}>My Markets</h1>
        <p style={{ fontSize:14, color:'#4a5a63' }}>All prediction markets you've created</p>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:200 }}>
          <Search size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#4a5a63' }}/>
          <input placeholder="Search markets..." value={search} onChange={e=>setSearch(e.target.value)} style={{ paddingLeft:36 }}/>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {['all','active','resolved','cancelled'].map(f=>(
            <button key={f} onClick={()=>setFilter(f)} className="btn" style={{ padding:'8px 16px', fontSize:12, background:filter===f?'rgba(0,197,102,.1)':'rgba(255,255,255,.04)', color:filter===f?'#00c566':'#4a5a63', border:filter===f?'1px solid rgba(0,197,102,.2)':'1px solid rgba(255,255,255,.08)', textTransform:'capitalize' }}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#4a5a63' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:48 }}>
          <Filter size={32} style={{ color:'#4a5a63', marginBottom:12 }}/>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>No markets found</div>
          <div style={{ fontSize:13, color:'#4a5a63' }}>Try adjusting your filters or create a new market</div>
        </div>
      ) : filtered.map(m => <MarketCard key={m.id} market={m}/>)}
    </div>
  );
}
